import SlideOver from "@components/ui/SlideOver";
import { badgeForStatus } from "@constants/agentSim";
import { parseTranscript, roleLabel } from "./transcriptUtils";

function Transcript({ transcript }) {
  const turns = parseTranscript(transcript);
  if (turns.length === 0) return null;
  return (
    <div className="space-y-2 bg-gray-50 border rounded p-2">
      {turns.map((t, i) => {
        const isAgent = t.role === "agent" || t.role === "assistant";
        return (
          <div key={i} className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 ${
                isAgent ? "bg-white border border-gray-200" : "bg-blue-600 text-white"
              }`}
            >
              <div
                className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${
                  isAgent ? "text-gray-400" : "text-blue-100"
                }`}
              >
                {roleLabel(t.role)}
              </div>
              <div className="text-xs whitespace-pre-wrap break-words">{t.message}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResultCard({ r }) {
  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded ${r.passed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {r.passed ? "PASS" : "FAIL"}
        </span>
        <span className="text-sm text-gray-500 font-mono">{r.scenario_id}</span>
      </div>
      {r.failed_assertion && <div className="text-sm text-red-700">Failed: {r.failed_assertion}</div>}
      {r.error && <div className="text-sm text-red-700">Error: {r.error}</div>}
      <ul className="text-sm space-y-1">
        {(r.assertion_results || []).map((a, i) => (
          <li key={i}>
            <span className={a.passed ? "text-green-600" : "text-red-600"}>{a.passed ? "✓" : "✗"}</span>{" "}
            {a.description || a.type}
            {!a.passed && a.detail && <span className="text-gray-500"> — {a.detail}</span>}
          </li>
        ))}
      </ul>
      {r.transcript && <Transcript transcript={r.transcript} />}
      {r.advisory_rating && (
        <div className="text-xs text-gray-500">
          Advisory GPT rating (does not gate pass/fail): overall_score={r.advisory_rating.overall_score ?? "—"}
        </div>
      )}
    </div>
  );
}

export default function RunDetailDrawer({ open, onClose, run, detail, loading }) {
  const badge = run ? badgeForStatus(run.status) : null;
  return (
    <SlideOver open={open} onClose={onClose} title={run ? `Run · ${run.purpose || ""}` : "Run"}>
      {loading && <div className="text-sm text-gray-500">Loading…</div>}
      {!loading && detail && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-sm">
            {badge && <span className={`px-2 py-0.5 rounded ${badge.className}`}>{badge.label}</span>}
            <span>passed {detail.run.scenarios_passed ?? 0} / failed {detail.run.scenarios_failed ?? 0}</span>
          </div>
          {(detail.results || []).map((r) => <ResultCard key={r.id} r={r} />)}
          {(detail.results || []).length === 0 && <div className="text-sm text-gray-500">No results.</div>}
        </div>
      )}
    </SlideOver>
  );
}
